import {
  Box,
  TextField,
  Typography,
  Stack,
  Link,
  Switch,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useBooking } from '../../../../context/bookingContext';
import { useAuth } from '../../../../context/authContext';
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { isDevelopmentMode, getRandomTestCustomerData } from '../../../../util/devDataHelper';
import useUser from '../../../../hooks/useUser';
import useWalkInSubmissions from '../../../../hooks/useWalkInSubmissions';
import useCustomer from '../../../../hooks/useCustomer';
import { useDebounce } from '../../../../hooks/useDebounce';
import HistoryIcon from '@mui/icons-material/History';
import BookmarkIcon from '@mui/icons-material/Bookmark';

export const validateInternationalPhone = phone => {
  const normalized = String(phone ?? '').trim();
  if (!/^\+\d+$/.test(normalized)) return false;
  return normalized.length >= 7 && normalized.length <= 16;
};

const CUSTOMER_NAME_REGEX = /^[A-Za-z\u00C0-\u024F\u0300-\u036F\u1E00-\u1EFF\s-]+$/u;

// Human-readable German labels used in validation messages
const FIELD_LABELS = {
  mail: 'E-Mail',
  firstName: 'Vorname',
  lastName: 'Nachname',
  phone: 'Telefon',
  street: 'Stra\u00DFe und Hausnummer',
  postcode: 'Postleitzahl',
  city: 'Stadt',
  birthdate: 'Geburtsdatum',
};

// Reduces any phone input to digits only, keeping just the international prefix.
// Spaces, separators, brackets and duplicate plus signs are stripped, the trunk
// zero written as '(0)' after the country code is dropped, and a leading '00' is
// converted into the '+' it stands for. A value that carries no international
// prefix at all keeps none — a plus is never invented for it.
//   '+49 176 / 1234-5678' -> '+4917612345678'
//   '+49 (0)176 1234567'  -> '+491761234567'
//   '0049 176 1234567'    -> '+491761234567'
//   '0176 1234567'        -> '01761234567'
const normalizePhoneInput = phone => {
  // Only a zero in brackets is unambiguously the trunk prefix, and only when a
  // country code precedes it
  const withoutTrunkZero = String(phone ?? '').replace(/([+\d])\s*\(\s*0\s*\)/, '$1');
  const digits = withoutTrunkZero.replace(/\D/g, '');
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  return withoutTrunkZero.trimStart().startsWith('+') ? `+${digits}` : digits;
};

// Shortest search term the customer lookup accepts; mirrors the backend guard.
const CUSTOMER_LOOKUP_MIN_LENGTH = 3;

// Label shown for a looked-up customer in the suggestion list.
const customerLookupLabel = customer =>
  customer.companyName ||
  `${customer.firstName || ''} ${customer.lastName || ''}`.trim() ||
  customer.email ||
  '';

const CustomerData = forwardRef(function CustomerData({ onWalkInSubmissionSelected }, ref) {
  const {
    customerData,
    setCustomerData,
    customerDataVersion,
    selectedDate,
    selectedTime,
    selectedBranch,
    applyCustomerData,
    setDefaultComment,
  } = useBooking();
  const { checkRight } = useAuth();
  const { getDefaultBookingData } = useUser();
  const { getPendingWalkInSubmissions } = useWalkInSubmissions();
  const { lookupCustomersForBooking } = useCustomer();
  const [localErrors, setLocalErrors] = useState({});
  const [localValues, setLocalValues] = useState(customerData);
  const [savedEntry, setSavedEntry] = useState(null);
  const [defaultBookingData, setDefaultBookingData] = useState(null);
  const [isWalkIn, setIsWalkIn] = useState(Boolean(customerData?.isWalkIn));
  const [walkInSubmissions, setWalkInSubmissions] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isLookingUpCustomers, setIsLookingUpCustomers] = useState(false);
  const appliedCustomerDataVersion = useRef(customerDataVersion);
  // Counter used to drop responses of superseded lookup requests
  const customerLookupRequestId = useRef(0);

  // When customer data is replaced wholesale from an external source (e.g. copied from a
  // present customer), resync the local input values so the form reflects the new data.
  useEffect(() => {
    if (appliedCustomerDataVersion.current === customerDataVersion) return;
    appliedCustomerDataVersion.current = customerDataVersion;
    setLocalValues(customerData);
    setLocalErrors({});
    setIsWalkIn(Boolean(customerData?.isWalkIn));
  }, [customerDataVersion, customerData]);

  // Key used to read previously stored customer data
  const LAST_CUSTOMER_DATA_KEY = 'fz:lastCustomerData';

  useEffect(() => {
    if (isDevelopmentMode() && !customerData.firstName) {
      const testData = getRandomTestCustomerData();
      setLocalValues(testData);
      setCustomerData(testData);
    }
  }, []);

  // On mount, check if there is a recent saved customer entry (<= 15 minutes old)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_CUSTOMER_DATA_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.timestamp || !parsed?.data) return;
      const isFresh = Date.now() - parsed.timestamp <= 15 * 60 * 1000;
      if (isFresh) {
        setSavedEntry(parsed);
      }
    } catch (e) {
      // ignore malformed storage content
    }
  }, []);

  // Cache the birthdate requirement check
  const isBirthdateRequired = useMemo(() => {
    return !checkRight('internalBooking:special:nobirthdate');
  }, [checkRight]);

  const canUseNonStandardPostcode = useMemo(() => {
    return checkRight('internalBooking:special:nonStandardPostcode');
  }, [checkRight]);

  // Dev/power-user feature: inject pre-configured default customer data and comment.
  const canUseDefaultCustomerData = useMemo(() => {
    return checkRight('internalBooking:special:defaultCustomerData');
  }, [checkRight]);

  // Load the user's configured default booking data once when they hold the right.
  // Guarded by the right so non-privileged users never trigger a 403 escalation.
  useEffect(() => {
    if (!canUseDefaultCustomerData) return;
    let cancelled = false;
    const fetchDefault = async () => {
      const data = await getDefaultBookingData();
      if (!cancelled) setDefaultBookingData(data);
    };
    fetchDefault();
    return () => {
      cancelled = true;
    };
  }, [canUseDefaultCustomerData]);

  // True when there is actually something configured to inject.
  const hasDefaultBookingData = useMemo(() => {
    if (!defaultBookingData) return false;
    const cd = defaultBookingData.customerData || {};
    const hasCustomerFields = Object.values(cd).some(v => String(v ?? '').trim() !== '');
    return hasCustomerFields || String(defaultBookingData.comment ?? '').trim() !== '';
  }, [defaultBookingData]);

  // Determine whether to show Walk-In switch based on start time window [-30min, +60min]
  const showWalkInSwitch = useMemo(() => {
    // Once walk-in mode is active (e.g. enabled by taking over a pre-submitted
    // entry) keep the switch visible regardless of the time window so staff can
    // still turn it off to enter the full data set.
    if (isWalkIn) return true;
    if (!selectedDate || !selectedTime) return false;
    const [h, m] = String(selectedTime).split(':').map(Number);
    const start = dayjs(selectedDate)
      .hour(h || 0)
      .minute(m || 0)
      .second(0)
      .millisecond(0);
    const now = dayjs();
    return start.isAfter(now.subtract(30, 'minute')) && start.isBefore(now.add(40, 'minute'));
  }, [selectedDate, selectedTime, isWalkIn]);

  // Customers can pre-submit their data themselves; staff pick it from a dropdown.
  // Guarded by the booking-create read right so non-privileged users never trigger
  // a 403 auth escalation.
  const canPickWalkInSubmission = useMemo(() => {
    return checkRight('internalBooking:create:read');
  }, [checkRight]);

  // Surface pre-submitted walk-in customers whenever there is something to pick.
  // Intentionally NOT gated behind the Walk-In switch or the booking time window:
  // staff must always be able to take over a customer's self-submitted data as
  // soon as it exists for the selected location.
  const showWalkInSubmissionDropdown = canPickWalkInSubmission && walkInSubmissions.length > 0;

  // Load the still-valid pre-submitted walk-in customers for the branch selected in
  // the create-booking modal whenever staff are allowed to pick them.
  useEffect(() => {
    if (!canPickWalkInSubmission || !selectedBranch) {
      setWalkInSubmissions([]);
      return;
    }
    let cancelled = false;
    const fetchSubmissions = async () => {
      try {
        const res = await getPendingWalkInSubmissions(selectedBranch);
        if (!cancelled) setWalkInSubmissions(res?.submissions || []);
      } catch (e) {
        // Non-blocking: staff can always type the data manually.
        if (!cancelled) setWalkInSubmissions([]);
      }
    };
    fetchSubmissions();
    return () => {
      cancelled = true;
    };
  }, [canPickWalkInSubmission, selectedBranch]);

  // Take over a pre-submitted customer 1:1 into the name and mail fields.
  const handleSelectWalkInSubmission = useCallback(
    (_event, submission) => {
      if (!submission) {
        // Clearing the selection returns the form to the normal full-data mode.
        setIsWalkIn(false);
        setLocalValues(prev => ({ ...prev, isWalkIn: false }));
        setCustomerData(prev => ({ ...prev, isWalkIn: false }));
        if (onWalkInSubmissionSelected) onWalkInSubmissionSelected(null);
        return;
      }
      // Pre-submitted entries only carry the reduced walk-in data set (name +
      // mail). Taking one over must put the form into walk-in mode so validation
      // requires only those fields — otherwise it would demand phone, address
      // and birthdate the customer never provided.
      setIsWalkIn(true);
      setLocalValues(prev => ({
        ...prev,
        firstName: submission.firstName,
        lastName: submission.lastName,
        mail: submission.mail,
        isWalkIn: true,
      }));
      setCustomerData(prev => ({
        ...prev,
        firstName: submission.firstName,
        lastName: submission.lastName,
        mail: submission.mail,
        isWalkIn: true,
      }));
      setLocalErrors(prev => ({ ...prev, firstName: '', lastName: '', mail: '' }));
      if (onWalkInSubmissionSelected) onWalkInSubmissionSelected(submission.id);
    },
    [setCustomerData, onWalkInSubmissionSelected]
  );

  // Special right: while typing the email address, stored customers are searched
  // and offered as suggestions that fill the rest of the form. Guarded so users
  // without the right neither see the dropdown nor trigger a 403.
  const canLookupCustomers = useMemo(() => {
    return checkRight('customer:special:bookingLookup');
  }, [checkRight]);

  const runCustomerLookup = useCallback(
    async term => {
      const search = String(term ?? '').trim();
      // Bumping the counter also invalidates a still running request, so its
      // result can never land after the search term became too short again.
      const requestId = customerLookupRequestId.current + 1;
      customerLookupRequestId.current = requestId;
      if (!canLookupCustomers || search.length < CUSTOMER_LOOKUP_MIN_LENGTH) {
        setCustomerSuggestions([]);
        setIsLookingUpCustomers(false);
        return;
      }
      setIsLookingUpCustomers(true);
      try {
        const res = await lookupCustomersForBooking(search);
        if (requestId !== customerLookupRequestId.current) return;
        setCustomerSuggestions(res?.customers || []);
      } catch (e) {
        // Non-blocking: staff can always type the customer data manually.
        if (requestId === customerLookupRequestId.current) setCustomerSuggestions([]);
      } finally {
        if (requestId === customerLookupRequestId.current) setIsLookingUpCustomers(false);
      }
    },
    [canLookupCustomers, lookupCustomersForBooking]
  );

  const debouncedCustomerLookup = useDebounce(runCustomerLookup, 300);

  // Take over a stored customer into the whole form. The customer master data
  // holds no birthdate, so an already entered one is kept.
  const handleSelectLookupCustomer = useCallback(
    (_event, option) => {
      // freeSolo hands over the raw string when staff just type an address
      if (!option || typeof option === 'string') return;
      applyCustomerData({
        firstName: option.firstName || '',
        lastName: option.lastName || '',
        mail: option.email || '',
        phone: normalizePhoneInput(option.phone),
        street: option.street || '',
        postcode: option.postalCode || '',
        city: option.city || '',
        companyName: option.companyName || '',
        birthdate: localValues.birthdate || '',
      });
      setCustomerSuggestions([]);
      setLocalErrors({});
      // A previously taken-over pre-submitted walk-in entry is no longer the
      // source of this booking, so it must not be consumed on save.
      if (onWalkInSubmissionSelected) onWalkInSubmissionSelected(null);
      toast.success(`Kundendaten von ${customerLookupLabel(option)} übernommen`);
    },
    [applyCustomerData, localValues.birthdate, onWalkInSubmissionSelected]
  );

  const validateName = useCallback(name => {
    return CUSTOMER_NAME_REGEX.test(name);
  }, []);

  const validatePhone = useCallback(phone => validateInternationalPhone(phone), []);

  const validatePostcode = useCallback(postcode => {
    const normalized = String(postcode ?? '').trim();
    if (!normalized) return false;
    if (canUseNonStandardPostcode) {
      return /^[0-9A-Za-z][0-9A-Za-z -]{0,19}$/.test(normalized);
    }
    // allow numeric postal codes with variable length (3–10 digits)
    return /^\d{3,10}$/.test(normalized);
  }, [canUseNonStandardPostcode]);

  const validateEmail = useCallback(email => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  const validateField = useCallback(
    (field, value) => {
      switch (field) {
        case 'mail':
          return value
            ? validateEmail(value)
              ? ''
              : 'Bitte gebe eine gültige E-Mail-Adresse ein'
            : '';
        case 'firstName':
        case 'lastName':
          return value
            ? validateName(value)
              ? ''
              : 'Nur Buchstaben, Akzente, Bindestriche und Leerzeichen erlaubt'
            : '';
        case 'phone':
          return value
            ? validatePhone(value)
              ? ''
              : 'Bitte gebe eine gültige internationale Telefonnummer ein (z.B. +491234567890)'
            : '';
        case 'postcode':
          return value
            ? validatePostcode(value)
              ? ''
              : canUseNonStandardPostcode
                ? 'Bitte gebe eine gültige Postleitzahl ein (z.B. 12345 oder SW1A 1AA)'
                : 'Bitte gebe eine gültige Postleitzahl (3–10 Ziffern) ein'
            : '';
        case 'birthdate':
          return value
            ? dayjs(value).isValid()
              ? ''
              : 'Bitte geben Sie ein gültiges Geburtsdatum ein'
            : '';
        default:
          return '';
      }
    },
    [validateEmail, validateName, validatePhone, validatePostcode, canUseNonStandardPostcode]
  );

  const updateContext = useCallback(
    (field, value) => {
      // Convert birthdate to ISO format for backend
      let processedValue = value;
      if (field === 'birthdate' && value) {
        const date = dayjs(value);
        if (date.isValid()) {
          processedValue = date.format('YYYY-MM-DD');
        }
      }

      setCustomerData(prev => ({
        ...prev,
        [field]: processedValue,
      }));
    },
    [setCustomerData]
  );

  // Persist walk-in flag to booking context when toggled. Keep localValues in
  // sync as well, because validateAndCommit/collectValidation read the walk-in
  // flag from localValues; otherwise walk-in bookings would wrongly require
  // phone, address and birthdate.
  const handleWalkInToggle = useCallback(
    (_, checked) => {
      setIsWalkIn(checked);
      setLocalValues(prev => ({ ...prev, isWalkIn: checked }));
      setCustomerData(prev => ({ ...prev, isWalkIn: checked }));
    },
    [setCustomerData]
  );

  const handleChange = useCallback(
    field => event => {
      const value = field === 'phone' ? normalizePhoneInput(event.target.value) : event.target.value;

      // Update local state immediately for responsive UI
      setLocalValues(prev => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  // Email input of the lookup-enabled autocomplete. Only real keystrokes update
  // the value and trigger a search; MUI's own resets after a selection are ignored.
  const handleMailInputChange = useCallback(
    (_event, value, reason) => {
      if (reason !== 'input') return;
      setLocalValues(prev => ({ ...prev, mail: value }));
      debouncedCustomerLookup(value);
    },
    [debouncedCustomerLookup]
  );

  const handleBlur = useCallback(
    field => event => {
      const value = field === 'phone' ? normalizePhoneInput(event.target.value) : event.target.value;

      // Validate and update context on blur
      const error = validateField(field, value);
      setLocalErrors(prev => ({
        ...prev,
        [field]: error,
      }));

      updateContext(field, value);
    },
    [validateField, updateContext]
  );

  const handleUseSaved = useCallback(() => {
    if (!savedEntry?.data) return;
    // Fill both local UI state and the booking context
    setLocalValues(savedEntry.data);
    setCustomerData(savedEntry.data);
    setLocalErrors({});
  }, [savedEntry, setCustomerData]);

  // Inject the user's configured default customer data and seed the booking comment.
  const handleUseDefault = useCallback(() => {
    if (!defaultBookingData) return;
    const customer = defaultBookingData.customerData || {};
    // applyCustomerData replaces the whole set and bumps customerDataVersion,
    // which resyncs the local inputs via the version effect above.
    applyCustomerData(customer);
    setDefaultComment(defaultBookingData.comment || '');
    setLocalErrors({});
    toast.success('Standard-Kundendaten übernommen');
  }, [defaultBookingData, applyCustomerData, setDefaultComment]);

  // Validate every relevant field and collect both inline errors and a
  // human-readable list of problems. A phone value of just '+' counts as empty.
  const collectValidation = useCallback(() => {
    const values = localValues || {};
    const walkIn = Boolean(values.isWalkIn);
    const errors = {};
    const messages = [];

    const isPhoneEmpty = value => {
      const normalized = String(value ?? '').trim();
      return normalized === '' || normalized === '+';
    };

    const requireField = (field, isEmpty = value => !String(value ?? '').trim()) => {
      if (isEmpty(values[field])) {
        errors[field] = `${FIELD_LABELS[field]} ist erforderlich`;
        messages.push(`${FIELD_LABELS[field]} fehlt`);
        return false;
      }
      return true;
    };

    if (requireField('mail') && !validateEmail(values.mail)) {
      errors.mail = 'Bitte gebe eine gültige E-Mail-Adresse ein';
      messages.push('E-Mail ist ungültig');
    }
    if (requireField('firstName') && !validateName(values.firstName)) {
      errors.firstName = 'Nur Buchstaben, Akzente, Bindestriche und Leerzeichen erlaubt';
      messages.push('Vorname ist ungültig');
    }
    if (requireField('lastName') && !validateName(values.lastName)) {
      errors.lastName = 'Nur Buchstaben, Akzente, Bindestriche und Leerzeichen erlaubt';
      messages.push('Nachname ist ungültig');
    }

    if (!walkIn) {
      if (requireField('phone', isPhoneEmpty) && !validatePhone(values.phone)) {
        errors.phone =
          'Bitte gebe eine gültige internationale Telefonnummer ein (z.B. +491234567890)';
        messages.push('Telefon ist ungültig');
      }
      requireField('street');
      if (requireField('postcode') && !validatePostcode(values.postcode)) {
        errors.postcode = canUseNonStandardPostcode
          ? 'Bitte gebe eine gültige Postleitzahl ein (z.B. 12345 oder SW1A 1AA)'
          : 'Bitte gebe eine gültige Postleitzahl (3–10 Ziffern) ein';
        messages.push('Postleitzahl ist ungültig');
      }
      requireField('city');
      if (isBirthdateRequired) {
        if (requireField('birthdate') && !dayjs(values.birthdate).isValid()) {
          errors.birthdate = 'Bitte geben Sie ein gültiges Geburtsdatum ein';
          messages.push('Geburtsdatum ist ungültig');
        }
      } else if (values.birthdate && !dayjs(values.birthdate).isValid()) {
        errors.birthdate = 'Bitte geben Sie ein gültiges Geburtsdatum ein';
        messages.push('Geburtsdatum ist ungültig');
      }
    }

    return { errors, messages };
  }, [
    localValues,
    validateEmail,
    validateName,
    validatePhone,
    validatePostcode,
    canUseNonStandardPostcode,
    isBirthdateRequired,
  ]);

  // Commit the latest local values into the booking context and validate them.
  // Returns { isValid, messages, data } so the parent can always surface what
  // is missing instead of leaving the "Weiter" button silently disabled.
  const validateAndCommit = useCallback(() => {
    const data = { ...localValues };
    setCustomerData(prev => ({ ...prev, ...data }));
    const { errors, messages } = collectValidation();
    setLocalErrors(errors);
    return { isValid: messages.length === 0, messages, data };
  }, [localValues, collectValidation, setCustomerData]);

  useImperativeHandle(ref, () => ({ validateAndCommit }), [validateAndCommit]);

  return (
    <Box sx={{ width: '100%' }}>
      {savedEntry && (
        <Box sx={{ mb: 1 }}>
          <Link
            component='button'
            variant='body2'
            onClick={handleUseSaved}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <HistoryIcon fontSize='small' />
            Letzte Kundendaten übernehmen
          </Link>
        </Box>
      )}
      {canUseDefaultCustomerData && hasDefaultBookingData && (
        <Box sx={{ mb: 1 }}>
          <Link
            component='button'
            variant='body2'
            onClick={handleUseDefault}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <BookmarkIcon fontSize='small' />
            Standard-Kundendaten übernehmen
          </Link>
        </Box>
      )}
      <Typography variant='h6' gutterBottom>
        Kundendaten
      </Typography>
      <Stack spacing={2}>
        {showWalkInSubmissionDropdown && (
          <Autocomplete
            options={walkInSubmissions}
            getOptionLabel={option => `${option.firstName} ${option.lastName}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleSelectWalkInSubmission}
            noOptionsText='Keine vorab übermittelten Kundendaten'
            renderInput={params => (
              <TextField
                {...params}
                label='Vorab übermittelte Kundendaten übernehmen'
                color='primary'
                helperText='Vom Kunden selbst angegebene Daten (gültig für 20 Minuten)'
              />
            )}
          />
        )}
        {canLookupCustomers ? (
          <Autocomplete
            freeSolo
            disableClearable
            // The input is the email field itself, the selection only fills the form
            value={null}
            inputValue={localValues.mail || ''}
            onInputChange={handleMailInputChange}
            onChange={handleSelectLookupCustomer}
            options={customerSuggestions}
            // Results are already filtered by the backend
            filterOptions={options => options}
            getOptionLabel={option => (typeof option === 'string' ? option : option.email || '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLookingUpCustomers}
            noOptionsText='Keine passenden Kunden gefunden'
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant='body2'>{customerLookupLabel(option)}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {[option.email, option.city, option.customerNumber ? `Nr. ${option.customerNumber}` : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label='E-Mail'
                type='email'
                onBlur={handleBlur('mail')}
                fullWidth
                required
                color='primary'
                error={!!localErrors.mail}
                helperText={localErrors.mail || 'Kunde suchen, um die Daten zu übernehmen'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLookingUpCustomers ? <CircularProgress color='inherit' size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : (
          <TextField
            label='E-Mail'
            type='email'
            value={localValues.mail || ''}
            onChange={handleChange('mail')}
            onBlur={handleBlur('mail')}
            fullWidth
            required
            color='primary'
            error={!!localErrors.mail}
            helperText={localErrors.mail}
          />
        )}
        <TextField
          label='Vorname'
          value={localValues.firstName || ''}
          onChange={handleChange('firstName')}
          onBlur={handleBlur('firstName')}
          fullWidth
          required
          color='primary'
          error={!!localErrors.firstName}
          helperText={localErrors.firstName}
        />
        <TextField
          label='Nachname'
          value={localValues.lastName || ''}
          onChange={handleChange('lastName')}
          onBlur={handleBlur('lastName')}
          fullWidth
          required
          color='primary'
          error={!!localErrors.lastName}
          helperText={localErrors.lastName}
        />

        {showWalkInSwitch && (
          <FormControlLabel
            control={<Switch checked={isWalkIn} onChange={handleWalkInToggle} color='primary' />}
            label='Walk-In Kunde'
          />
        )}

        {!isWalkIn && (
          <>
            <TextField
              label='Telefon'
              value={localValues.phone || '+'}
              onChange={handleChange('phone')}
              onBlur={handleBlur('phone')}
              fullWidth
              required
              color='primary'
              error={!!localErrors.phone}
              helperText={localErrors.phone}
              inputProps={{
                onFocus: e => {
                  if (e.target.value === '+') {
                    e.target.setSelectionRange(1, 1);
                  }
                },
              }}
            />
            <TextField
              label='Straße und Hausnummer'
              value={localValues.street || ''}
              onChange={handleChange('street')}
              onBlur={handleBlur('street')}
              fullWidth
              required
              color='primary'
            />
            <TextField
              label='Postleitzahl'
              value={localValues.postcode || ''}
              onChange={handleChange('postcode')}
              onBlur={handleBlur('postcode')}
              fullWidth
              required
              color='primary'
              error={!!localErrors.postcode}
              helperText={localErrors.postcode}
              inputProps={{
                maxLength: canUseNonStandardPostcode ? 20 : 10,
                inputMode: canUseNonStandardPostcode ? 'text' : 'numeric',
              }}
            />
            <TextField
              label='Stadt'
              value={localValues.city || ''}
              onChange={handleChange('city')}
              onBlur={handleBlur('city')}
              fullWidth
              required
              color='primary'
            />
            <TextField
              label='Firma'
              value={localValues.companyName || ''}
              onChange={handleChange('companyName')}
              onBlur={handleBlur('companyName')}
              fullWidth
              color='primary'
            />
            <TextField
              label='Geburtsdatum'
              type='date'
              value={localValues.birthdate || ''}
              onChange={handleChange('birthdate')}
              onBlur={handleBlur('birthdate')}
              fullWidth
              required={isBirthdateRequired}
              color='primary'
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: '1900-01-01',
                max: dayjs().format('YYYY-MM-DD'),
              }}
            />
          </>
        )}
      </Stack>
    </Box>
  );
});

export default CustomerData;
